/**
 * @module experience/shop/hardgoods
 * @copyright 1994-2017 T-Mobile US, Inc. All Rights Reserved.
 */
/** */

import { DeviceSpecification } from '@experience/shop/hardgoods/interfaces/compare-hardgoods.interface';
import { SkuPrice, Specification } from '@support/api/common/product.interface';
import { BrowseDeviceModel } from '@experience/shop/hardgoods/models/browse-device.model';
import { ObjectService } from '@support/state-object-hash-utilities/services/object.service';

export class CompareDeviceModel {
  /**
   * name variable stores the name of the product
   */
  public name: string;

  /**
   * price variable stores the final calculated price
   */
  public price: string;

  /**
   * originalPriceArray contains the product original price array
   */
  public originalPriceArray: SkuPrice[];

  /**
   * rowLabel stores product categories for comparison
   * @type {Set<string>}
   */
  public rowLabel: Set<string> = new Set<string>();

  /**
   * deviceSpecifications stores product specifications
   * @type {{}}
   */
  public deviceSpecifications: DeviceSpecification = {};

  /**
   * imageUrl stores the image url
   */
  public imageUrl: string;

  /**
   * isChecked variable for selecting/de-selecting the product on html view for comparison
   * @type {boolean}
   */
  public isChecked: boolean = true;

  /**
   * list price of the device
   */
  public listPrice: string;
  /**
   * service threshold price of the device
   */
  public serviceThresholdPrice: string;

  /**
   * service threshold price of the device
   */
  public withoutQualifyingPlan: string;

  /**
   * star image path
   */
  public averageStarRating: string;

  constructor(
    deviceInfo: BrowseDeviceModel,
    private objectService: ObjectService
  ) {
    this.objectService = objectService;
    this.createDeviceSpecifications(this.objectService.get(deviceInfo, 'specifications'));
    this.getDeviceDetails(deviceInfo);
  }

  /**
   * createDeviceSpecifications is creating device specification array
   * @param deviceSpecifications
   */
  public createDeviceSpecifications(deviceSpecifications: Specification[]): void {
    if (deviceSpecifications) {
      for (let i = 0; i < deviceSpecifications.length; i++) {
        const specificationValue = this.objectService.get(
          deviceSpecifications[i],
          'specificationValues.0.value'
        );
        if (specificationValue && specificationValue.length > 0) {
          const specificationName = this.objectService.get(
            deviceSpecifications[i],
            'specificationValues.0.name'
          );
          this.rowLabel.add(specificationName);
          this.deviceSpecifications[specificationName] = specificationValue;
        }
      }
    }
  }

  /**
   * getDeviceDetails is called for creating product details
   * @param deviceInfo
   */
  public getDeviceDetails(deviceInfo: BrowseDeviceModel): void {
    this.name = this.objectService.get(deviceInfo, 'familyName');
    this.imageUrl = this.objectService.get(deviceInfo, 'itemDetails.0.imageUrl');
    this.price = this.objectService.get(deviceInfo, 'itemDetails.0.payNowPrice');
    this.listPrice = this.objectService.get(deviceInfo, 'itemDetails.0.listPrice');
    this.serviceThresholdPrice = this.objectService.get(
      deviceInfo,
      'itemDetails.0.serviceThresholdPrice'
    );
    this.averageStarRating = this.objectService.get(
      deviceInfo,
      'itemDetails.0.averageOverallRating'
    );
  }
}
